import useIsMobile from '@/hooks/use-is-mobile';
import { Box, IconInfoCircle, Link, LinkProps, Popover, Text, TextProps } from '@cmpsr/components';
import React, { FC } from 'react';
import ReactMarkdown, { ExtraProps, Options } from 'react-markdown';
import { QuestionTitleModal } from './components/Modal';
import { type QuestionTitleProps } from './types';
import { InfoButton } from './components/InfoButton';

function TitleMarkdown(props: Exclude<Options, 'components'>): React.ReactElement {
  return (
    <ReactMarkdown
      components={{
        p(props: TextProps & ExtraProps) {
          const { node, ...rest } = props;
          return <Text as="div" variant="text-header-M" mb={2} textAlign="left" {...rest} />;
        },
        a(props: LinkProps & ExtraProps) {
          const { node, ...rest } = props;
          return <Link target="_blank" textDecoration="underline" color="text-link-secondary-default" {...rest} />;
        },
      }}
      {...props}
    />
  );
}

export const QuestionTitle: FC<QuestionTitleProps> = ({
  question,
  whyWeAskExplanation,
  description,
  tooltip,
  whyWeAskModalDisclosure,
}) => {
  const isMobile = useIsMobile();

  return (
    <>
      <TitleMarkdown>{question}</TitleMarkdown>

      {description || tooltip ? (
        <Box>
          {description && (
            <Text as="span" variant="text-body-meta-regular" color="text-secondary" mr={2}>
              {description}
            </Text>
          )}
          {tooltip && (
            <Popover trigger={isMobile ? 'click' : 'hover'} closeOnBlur placement="bottom">
              <Popover.Trigger>
                <Box display="inline-block" alignSelf={{ base: 'flex-end', lg: 'left' }}>
                  <IconInfoCircle size="s" color="link-secondary" />
                </Box>
              </Popover.Trigger>
              <Popover.Content>
                <Popover.Arrow backgroundColor="text-secondary" />
                <Popover.Body
                  backgroundColor="text-secondary"
                  borderColor="transparent"
                  borderRadius="radii-1"
                  p="spacer-2"
                >
                  <Text variant="text-body-meta-regular" color="text-light" whiteSpace="normal">
                    {tooltip}
                  </Text>
                </Popover.Body>
              </Popover.Content>
            </Popover>
          )}
        </Box>
      ) : null}
      {whyWeAskExplanation ? <InfoButton onClick={whyWeAskModalDisclosure.onOpen} /> : null}

      <QuestionTitleModal
        modalIsOpen={whyWeAskModalDisclosure.isOpen}
        onClose={whyWeAskModalDisclosure.onClose}
        content={whyWeAskExplanation ?? ''}
      />
    </>
  );
};
